import { isEmpty } from 'lodash';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from '@shared/components/View';
import { MBImagePreviewModal } from '@shared/components/image';
import { Pill } from '@shared/components/pill';
import { FileUpload } from '@shared/types/fieldValues';
import { useBoolean } from '@shared/util/hooks';
import { isImage } from '@shared/util/media/type';
import { startViewTransition } from '@shared/util/startViewTransition';
import { ImageItem } from './ImageItem';
import styles from './ImageList.module.css';

interface FilesProps {
  files: FileUpload[];
  editable: boolean;
  onFileRemoved?: (file: FileUpload) => void;
  style?: CSSProperties;
  itemStyle?: CSSProperties;
  maxWidth?: number;
  maxHeight?: number;
  maxImages?: number;
}

export function ImageList({
  files,
  onFileRemoved,
  editable,
  style,
  itemStyle,
  maxWidth = 300,
  maxHeight = 150,
  maxImages = Infinity,
}: FilesProps) {
  const [isPreviewOpen, showPreview, hidePreview] = useBoolean(false);
  const [showIndex, setShowIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const images = useMemo(() => files.filter(isImage), [files]);
  const imagesToShow = useMemo(() => images.slice(0, maxImages), [images, maxImages]);

  const [renderImages, setRenderImages] = useState(imagesToShow);

  useEffect(() => {
    if (listRef.current) {
      startViewTransition(() => {
        setRenderImages(imagesToShow);
      }, listRef.current);
    }
  }, [imagesToShow]);

  const onPressImage = useCallback(
    (index: number) => {
      setShowIndex(index);
      showPreview();
    },
    [showPreview],
  );

  if (isEmpty(images)) {
    return null;
  }

  return (
    <>
      <View className={styles.imageList} style={style} gap={1} ref={listRef}>
        {renderImages.map((item, index) => (
          <ImageItem
            key={item.url}
            image={item}
            editable={editable}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            style={itemStyle}
            onPress={() => onPressImage(index)}
            onClear={() => onFileRemoved?.(item)}
          />
        ))}
        <ImageListFooter images={images} maxImages={maxImages} onPressImage={onPressImage} />
      </View>
      {isPreviewOpen && (
        <MBImagePreviewModal
          images={images}
          showIndex={showIndex}
          visible={isPreviewOpen}
          onClose={hidePreview}
        />
      )}
    </>
  );
}

function ImageListFooter({
  images,
  maxImages,
  onPressImage,
}: {
  images: FileUpload[];
  maxImages: number;
  onPressImage: (index: number) => void;
}) {
  const extraImagesCount = images.length - maxImages;
  if (extraImagesCount < 1) {
    return null;
  }
  return <Pill noLeftMargin onPress={() => onPressImage(maxImages)}>{`+${extraImagesCount}`}</Pill>;
}
